import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import toast from "react-hot-toast";

import KendoInput from "assets/theme/components/inputKendo";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { AdminQuotationApiService } from "api/adminQuotation.api";
import { cc } from "afms/theme/commandCenterTheme";
import { groupByOrg } from "utils/recipientGroups";

const OTHER = "__other__";

const FieldLabel = ({ children, required }) => (
  <SoftTypography
    variant="caption"
    fontWeight="bold"
    sx={{ display: "block", mb: 0.5, letterSpacing: 0.3, color: cc.ink }}
  >
    {children}
    {required && <span style={{ color: cc.red, marginLeft: 4 }}>*</span>}
  </SoftTypography>
);

FieldLabel.propTypes = { children: PropTypes.node.isRequired, required: PropTypes.bool };
FieldLabel.defaultProps = { required: false };

// SoftUI sets thead to display:block, which drops the header onto its own line
// and starts the body cells beside it instead of under it.
const tableHeadSx = {
  display: "table-header-group",
  "& .MuiTableCell-head": {
    bgcolor: "#f8fbff",
    color: cc.muted,
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    borderBottom: `1px solid ${cc.line}`,
    letterSpacing: 0.3,
    py: 1,
  },
};

const pad = (n) => String(n).padStart(2, "0");
const toDateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const asDateInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : toDateStr(d);
};

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

let lineKey = 0;
const nextKey = () => {
  lineKey += 1;
  return `line-${lineKey}`;
};

// Keeps the source quote line on the row so the fields the dialog doesn't
// show — lineKind, partId, serviceId, the subscription block — survive the
// round trip. A "part" line that loses its partId stops reconciling against
// inventory.
const toEditableLine = (item) => ({
  key: nextKey(),
  source: item,
  name: item?.name ?? "",
  quantity: item?.quantity ?? 1,
  rate: item?.rate ?? 0,
});

const blankLine = () => ({ key: nextKey(), source: null, name: "", quantity: 1, rate: 0 });

const lineAmount = (line) => (Number(line.quantity) || 0) * (Number(line.rate) || 0);

/**
 * Bills a quote. Raises the invoice from the quote's own lines, emails it to a
 * contact at the customer organisation, and moves it to Open so it lands on
 * that org's /payables.
 *
 * The review step is the point: unlike Send Quote — which posts a document the
 * customer is free to ignore — this one is a demand for payment, so the vendor
 * gets to correct the number, the due date and the lines before it leaves.
 */
function SendInvoiceModal({ open, onClose, onSent, quote }) {
  const [recipients, setRecipients] = useState([]);
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [picked, setPicked] = useState("");
  const [otherEmail, setOtherEmail] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [lines, setLines] = useState([]);
  const [message, setMessage] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);
  const [embedPayOnline, setEmbedPayOnline] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !quote?.id) return undefined;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setLoadError(null);
      setPicked("");
      setOtherEmail("");
      setMessage("");
      setAttachPdf(true);
      setEmbedPayOnline(false);
      setRecipientInfo(null);

      try {
        // The quote detail rather than the listing row: same call the edit page
        // makes, so the lines carry their full shape.
        const [recipientsRes, quoteRes] = await Promise.all([
          AdminQuotationApiService.getRecipients(quote.id),
          AdminQuotationApiService.getQuotation(quote.id),
        ]);
        if (cancelled) return;

        const info = recipientsRes?.data?.data ?? null;
        const list = Array.isArray(info?.recipients) ? info.recipients : [];
        setRecipientInfo(info);
        setRecipients(list);
        // Preselect only when there's no ambiguity: a single contact overall,
        // or a single contact at the org actually being billed.
        const atCustomer = list.filter((r) => r.isQuoteCustomer);
        const obvious = atCustomer.length === 1 ? atCustomer : list;
        if (obvious.length === 1) setPicked(String(obvious[0].userProfileId));

        const full = quoteRes?.data?.data ?? quoteRes?.data ?? null;
        setDetail(full);

        const serial = full?.serialNumber || quote.serialNumber || quote.id;
        setInvoiceNumber(`INV-${serial}`);

        const due = asDateInput(full?.dueDate ?? quote.dueDate);
        setDueDate(due || toDateStr(new Date(Date.now() + 30 * 864e5)));

        const items = full?.items ?? quote.items ?? [];
        setLines(items.length ? items.map(toEditableLine) : [blankLine()]);
      } catch (error) {
        if (cancelled) return;
        setLoadError(
          error?.response?.data?.message || error?.message || "Failed to load this quote."
        );
        setRecipients([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, quote?.id]);

  const total = useMemo(
    () => lines.reduce((sum, line) => sum + lineAmount(line), 0),
    [lines]
  );

  const recipientGroups = useMemo(() => groupByOrg(recipients), [recipients]);

  // Server refuses this too — surfaced here so the vendor isn't told only after
  // filling the form in. Without a customer org the invoice reaches nobody's
  // /payables.
  //
  // Two distinct causes, and they need different fixes. The quote row's own
  // billingAddressId is always readable, but the address record behind it is
  // scoped by row-level security to the caller's org plus its granted client
  // orgs — so a quote billed to an org you have no client link to looks
  // identical to one with no billing address at all unless the server says
  // which it is.
  const customerOrgId =
    recipientInfo?.customerOrganisationId ??
    detail?.customerOrganisationId ??
    quote?.customerOrganisationId;
  const customerHidden = Boolean(recipientInfo?.customerOrganisationHidden);
  const missingBillingAddress = Boolean(recipientInfo?.missingBillingAddress);
  const missingCustomerOrg = !loading && !loadError && !customerOrgId;

  const updateLine = (key, patch) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLine = (key) => setLines((prev) => prev.filter((l) => l.key !== key));
  const addLine = () => setLines((prev) => [...prev, blankLine()]);

  const canSend =
    !sending &&
    !loading &&
    !loadError &&
    !missingCustomerOrg &&
    Boolean(picked) &&
    (picked !== OTHER || otherEmail.trim().length > 0) &&
    invoiceNumber.trim().length > 0 &&
    Boolean(dueDate) &&
    lines.length > 0 &&
    lines.every((l) => l.name.trim().length > 0 && Number(l.quantity) > 0) &&
    total > 0;

  const handleSend = async () => {
    if (!canSend) return;
    try {
      setSending(true);
      const items = lines.map((line) => {
        const quantity = Number(line.quantity) || 0;
        const rate = Number(line.rate) || 0;
        return {
          // Spread the source line first so lineKind / partId / serviceId /
          // subscription fields ride along untouched.
          ...(line.source ?? {}),
          id: undefined,
          name: line.name.trim().slice(0, 100),
          quantity,
          rate,
          price: Number((quantity * rate).toFixed(2)),
        };
      });

      const response = await AdminQuotationApiService.sendInvoice(quote.id, {
        userProfileId: picked === OTHER ? null : Number(picked),
        email: picked === OTHER ? otherEmail.trim() : null,
        message: message.trim() || null,
        attachPdf,
        embedPayOnline,
        vendorInvoiceNumber: invoiceNumber.trim(),
        dueDate,
        amount: Number(total.toFixed(2)),
        items,
      });

      const invoice = response?.data?.data ?? null;
      toast.success(invoice?.id ? `Invoice ${invoiceNumber.trim()} sent` : "Invoice sent");
      onSent?.();
      onClose?.();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to send invoice");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        <SoftTypography variant="h6" fontWeight="bold" sx={{ color: cc.ink }}>
          Send Invoice {quote?.id ? `— from Quote #${quote.id}` : ""}
        </SoftTypography>
        <SoftTypography variant="caption" color="text">
          Raises the invoice, emails it, and puts it on the customer&apos;s Payables.
        </SoftTypography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={sending}
          sx={{ position: "absolute", right: 8, top: 8, color: cc.muted }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: cc.page }}>
        {loading ? (
          <Skeleton variant="rectangular" height={280} animation="wave" />
        ) : (
          <>
            {loadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {loadError}
              </Alert>
            )}

            {missingCustomerOrg && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {missingBillingAddress || !customerHidden
                  ? `This quote has no billing address, so there's no organisation to bill.
                     Add one before invoicing it.`
                  : `This quote is billed to an organisation your org has no client link to.
                     The invoice can't reach their Payables until that link exists — ask an
                     admin to add them as a client organisation.`}
              </Alert>
            )}

            {quote?.invoiceId && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Invoice #{quote.invoiceId} is already linked to this quote — sending re-sends that
                invoice rather than raising a second one.
                {quote?.invoice?.status !== 1 &&
                  " It has already left draft, so the number, due date and lines below are shown for reference and won't be changed."}
              </Alert>
            )}

            <FieldLabel required>To</FieldLabel>
            {recipients.length === 0 && (
              <SoftTypography variant="caption" color="text" display="block" mb={1}>
                {customerHidden
                  ? `No client organisations are linked to your org yet, so there are no
                     contacts to choose from — enter an address below.`
                  : `No contacts on file at ${
                      recipientInfo?.customerOrganisationName ||
                      detail?.customerName ||
                      quote?.customerName ||
                      "this customer"
                    } — enter an address below.`}
              </SoftTypography>
            )}
            <select
              className="custom-select"
              name="recipient"
              value={picked}
              onChange={(e) => setPicked(e.target.value)}
            >
              <option value="">Select a recipient</option>
              {recipientGroups.map((group) => (
                <optgroup
                  key={group.id}
                  label={group.isQuoteCustomer ? `${group.name} (billed)` : group.name}
                >
                  {group.people.map((r) => (
                    <option value={String(r.userProfileId)} key={r.userProfileId}>
                      {r.name ? `${r.name} — ${r.email}` : r.email}
                    </option>
                  ))}
                </optgroup>
              ))}
              <option value={OTHER}>Someone else…</option>
            </select>

            {picked === OTHER && (
              <Box mt={1.5}>
                <FieldLabel required>Email address</FieldLabel>
                <KendoInput
                  type="email"
                  placeholder="name@company.com"
                  name="otherEmail"
                  value={otherEmail}
                  onChange={(e) => setOtherEmail(e.target.value)}
                />
              </Box>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={2}>
              <Box flex={1}>
                <FieldLabel required>Invoice number</FieldLabel>
                <KendoInput
                  name="invoiceNumber"
                  placeholder="INV-1042"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                />
              </Box>
              <Box flex={1}>
                <FieldLabel required>Due date</FieldLabel>
                <KendoInput
                  type="date"
                  name="dueDate"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </Box>
            </Stack>

            <Box mt={2.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                <FieldLabel required>Lines</FieldLabel>
                <SoftButton
                  variant="text"
                  color="info"
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={addLine}
                >
                  Add line
                </SoftButton>
              </Stack>

              <TableContainer sx={{ boxShadow: "none", bgcolor: "#fff", borderRadius: 1 }}>
                <Table size="small">
                  <TableHead sx={tableHeadSx}>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell align="right" width={90}>Qty</TableCell>
                      <TableCell align="right" width={120}>Rate</TableCell>
                      <TableCell align="right" width={110}>Amount</TableCell>
                      <TableCell width={48} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lines.map((line) => (
                      <TableRow key={line.key}>
                        <TableCell>
                          <KendoInput
                            name={`name-${line.key}`}
                            placeholder="What you're billing for"
                            value={line.name}
                            onChange={(e) => updateLine(line.key, { name: e.target.value })}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <KendoInput
                            type="number"
                            name={`qty-${line.key}`}
                            value={line.quantity}
                            onChange={(e) => updateLine(line.key, { quantity: e.target.value })}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <KendoInput
                            type="number"
                            name={`rate-${line.key}`}
                            value={line.rate}
                            onChange={(e) => updateLine(line.key, { rate: e.target.value })}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <SoftTypography variant="caption" fontWeight="bold">
                            {money(lineAmount(line))}
                          </SoftTypography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Remove line">
                            <span>
                              <IconButton
                                size="small"
                                disabled={lines.length === 1}
                                onClick={() => removeLine(line.key)}
                                aria-label="Remove line"
                                sx={{ color: lines.length === 1 ? undefined : cc.red }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack direction="row" justifyContent="flex-end" alignItems="baseline" spacing={2} mt={1.5}>
                <SoftTypography variant="caption" color="text" fontWeight="bold">
                  Total
                </SoftTypography>
                <SoftTypography variant="h6" fontWeight="bold" sx={{ color: cc.ink }}>
                  {money(total)}
                </SoftTypography>
              </Stack>
            </Box>

            <Box mt={2}>
              <FieldLabel>Message (optional)</FieldLabel>
              <KendoInput
                placeholder="A short note to include above the invoice"
                name="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Default-size Switch on purpose — size="small" leaves the thumb
                off-centre under the SoftUI theme. */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Switch
                checked={attachPdf}
                onChange={(e) => setAttachPdf(e.target.checked)}
                inputProps={{ "aria-label": "Attach the invoice as a PDF" }}
              />
              <Box>
                <SoftTypography variant="button" fontWeight="medium" sx={{ color: cc.ink }}>
                  Attach invoice as PDF
                </SoftTypography>
                <SoftTypography variant="caption" color="text" display="block">
                  Also stored on the invoice, so the customer can download it from Payables.
                </SoftTypography>
              </Box>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1.5} mt={1}>
              <Switch
                checked={embedPayOnline}
                onChange={(e) => setEmbedPayOnline(e.target.checked)}
                inputProps={{ "aria-label": "Embed a Pay Online button" }}
              />
              <Box>
                <SoftTypography variant="button" fontWeight="medium" sx={{ color: cc.ink }}>
                  Embed &ldquo;Pay Online&rdquo; button
                </SoftTypography>
                <SoftTypography variant="caption" color="text" display="block">
                  Adds this quote&apos;s checkout link so the customer can pay from the email.
                </SoftTypography>
              </Box>
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <SoftButton
          variant="outlined"
          color="dark"
          size="small"
          onClick={onClose}
          disabled={sending}
        >
          Cancel
        </SoftButton>
        <SoftButton
          variant="gradient"
          color="info"
          size="small"
          onClick={handleSend}
          disabled={!canSend}
          startIcon={sending ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {sending ? "Sending…" : "Send Invoice"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

SendInvoiceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSent: PropTypes.func,
  quote: PropTypes.shape({
    id: PropTypes.number,
    serialNumber: PropTypes.number,
    dueDate: PropTypes.string,
    customerName: PropTypes.string,
    customerOrganisationId: PropTypes.number,
    invoiceId: PropTypes.number,
    items: PropTypes.array,
  }),
};

SendInvoiceModal.defaultProps = { onSent: undefined, quote: null };

export default SendInvoiceModal;
